import { X } from 'lucide-react'
import type React from 'react'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

interface ModalProps {
    isOpen: boolean
    onClose: () => void
    title: string | React.ReactNode
    children: React.ReactNode
    className?: string
    width?: string
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, className = '', width }) => {
    const modalRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }

        if (isOpen) {
            document.body.style.overflow = 'hidden'
            document.addEventListener('keydown', handleEscape)
        }

        return () => {
            document.body.style.overflow = 'unset'
            document.removeEventListener('keydown', handleEscape)
        }
    }, [isOpen, onClose])

    if (!isOpen) return null

    if (typeof document === 'undefined') return null

    return createPortal(
        <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
                onClick={onClose}
            />

            {/* Content */}
            <div
                ref={modalRef}
                className={`relative w-full ${!width ? 'max-w-2xl' : ''} bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl rounded-[24px] shadow-2xl border border-white/60 dark:border-slate-700 ring-1 ring-white/50 dark:ring-white/10 transform transition-all duration-300 scale-100 flex flex-col max-h-[85vh] ${className}`}
                style={{ maxWidth: width }}
                role="dialog"
                aria-modal="true"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-800">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-slate-200 flex items-center gap-2">
                        {title}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto custom-scrollbar">{children}</div>
            </div>
        </div>,
        document.body,
    )
}
